import { useCallback, useMemo } from "react"

import type { MetricRecord } from "@/metrics/runtime"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import type { SofiaPageSummary } from "@/lib/covi/sofiaPageSummary"
import { openCoviWithSummary } from "@/lib/coviEvents"
import { useCoviSummaryMemory } from "@/hooks/useCoviSummaryMemory"
import { useRefreshingReferenceDate } from "@/hooks/useRefreshingReferenceDate"
import { usePublishSofiaPageSummary } from "@/hooks/useSofiaPageSummary"

import { buildRunsPageSummary } from "../utils/runsSummaryBuilder"
import { buildRunsPageSummaryMarkdown } from "../utils/runsSummaryMarkdown"
import { toRunsSummaryTimeframe } from "../utils/runsSummaryTimeframeFromRange"
import type { RunsPageSummaryRow } from "../utils/runsSummaryTypes"
import { selectAdaptiveRunsSummaryTimeframe } from "../utils/runsSummaryWindow"
import type { RunsKeysetFilters } from "./useRunsInfiniteQuery"
import { useRunsPageInsights } from "./useRunsPageInsights"

const SUMMARY_TITLE = "Simulated Conversations Summary"

interface UseRunsSummaryPublisherParams {
  filters: RunsKeysetFilters
  agentNames?: Record<string, string>
  enabled?: boolean
  metricDisplayNames?: Record<string, string>
  metricRecordsById?: Record<string, MetricRecord | undefined>
  personasById?: Record<string, { display_name?: string } | undefined>
  runs?: RunsPageSummaryRow[]
  simulations?: RunsPageSummaryRow[]
  testSetNames?: Record<string, string>
}

interface RunsSummaryLoadingState {
  enabled: boolean
  baselineDataAvailable: boolean
  baselineIsError: boolean
  baselineIsLoading: boolean
  behaviorDataAvailable: boolean
  behaviorEnabled: boolean
  behaviorIsError: boolean
  behaviorIsLoading: boolean
}

export function shouldShowRunsSummaryLoading({
  enabled,
  baselineDataAvailable,
  baselineIsError,
  baselineIsLoading,
  behaviorDataAvailable,
  behaviorEnabled,
  behaviorIsError,
  behaviorIsLoading,
}: RunsSummaryLoadingState): boolean {
  if (!enabled) {
    return true
  }

  const baselineIsPending =
    baselineIsLoading || (!baselineDataAvailable && !baselineIsError)
  const behaviorIsPending =
    behaviorEnabled &&
    (behaviorIsLoading || (!behaviorDataAvailable && !behaviorIsError))
  return baselineIsPending || behaviorIsPending
}

export function buildRunsSummaryFilters(
  filters: RunsKeysetFilters,
): RunsKeysetFilters {
  return {
    ...filters,
    // The summary timeframe owns its current/prior boundaries. Carrying the
    // table date range would silently truncate the comparison window.
    dateRange: undefined,
  }
}

function getCurrentOrganizationSlug(): string | null {
  if (typeof window === "undefined") {
    return null
  }
  return window.location.pathname.split("/").filter(Boolean)[0] ?? null
}

/**
 * Publishes the simulations page's narrative summary to Sofia, which opens the
 * chat on it as one of her messages.
 *
 * The page body renders nothing for it. The window follows the table's date
 * filter, falling back to one chosen adaptively from run volume. Nothing is
 * published while it is still loading, so Sofia never presents a summary that
 * would open empty.
 */
export function useRunsSummaryPublisher({
  agentNames,
  enabled = true,
  filters,
  metricDisplayNames,
  metricRecordsById,
  personasById,
  runs,
  simulations,
  testSetNames,
}: UseRunsSummaryPublisherParams): void {
  const { trackEvent } = useAnalytics()
  const summaryReferenceDate = useRefreshingReferenceDate(enabled)
  const summaryFilters = useMemo(
    () => buildRunsSummaryFilters(filters),
    [filters],
  )
  const { data, isError, isLoading } = useRunsPageInsights(summaryFilters, {
    enabled,
  })
  const organizationSlug = getCurrentOrganizationSlug()
  const { summaryPreferencesText } = useCoviSummaryMemory(organizationSlug)

  const buckets = useMemo(() => data?.buckets ?? [], [data?.buckets])
  const summaryRows = useMemo(
    () => (simulations && simulations.length > 0 ? simulations : (runs ?? [])),
    [runs, simulations],
  )
  const personaNames = useMemo(() => {
    const lookup: Record<string, string> = {}

    Object.entries(personasById ?? {}).forEach(([id, persona]) => {
      if (persona?.display_name) {
        lookup[id] = persona.display_name
      }
    })

    return lookup
  }, [personasById])
  // Sofia describes the window the table is filtered to, and picks one
  // adaptively when the range is unset or spans no window it reports on.
  const summaryTimeframe = toRunsSummaryTimeframe(filters.dateRange)
  const resolvedSummaryTimeframe = useMemo(
    () =>
      summaryTimeframe === "auto"
        ? selectAdaptiveRunsSummaryTimeframe(buckets, summaryReferenceDate)
        : summaryTimeframe,
    [buckets, summaryReferenceDate, summaryTimeframe],
  )
  const analysisWindowDays =
    resolvedSummaryTimeframe === "24h"
      ? 1
      : resolvedSummaryTimeframe === "7d"
        ? 7
        : 30
  const behaviorEnabled = enabled && data !== undefined
  const {
    data: behaviorData,
    isError: behaviorIsError,
    isLoading: behaviorIsLoading,
  } = useRunsPageInsights(summaryFilters, {
    analysisWindowDays,
    enabled: behaviorEnabled,
  })

  const summaryIsLoading = shouldShowRunsSummaryLoading({
    enabled,
    baselineDataAvailable: data !== undefined,
    baselineIsError: isError === true,
    baselineIsLoading: isLoading,
    behaviorDataAvailable: behaviorData !== undefined,
    behaviorEnabled,
    behaviorIsError: behaviorIsError === true,
    behaviorIsLoading,
  })
  const summary = useMemo(
    () =>
      buildRunsPageSummary(buckets, summaryTimeframe, summaryReferenceDate, {
        behaviorInsights: behaviorData?.simulationInsights,
        focusText: summaryPreferencesText,
        rows: summaryRows,
        lookups: {
          agentNames,
          metricDisplayNames,
          metricRecordsById,
          personaNames,
          testSetNames,
        },
      }),
    [
      agentNames,
      behaviorData?.simulationInsights,
      buckets,
      metricDisplayNames,
      metricRecordsById,
      personaNames,
      summaryPreferencesText,
      summaryReferenceDate,
      summaryRows,
      summaryTimeframe,
      testSetNames,
    ],
  )
  const summaryMarkdown = useMemo(
    () => buildRunsPageSummaryMarkdown(summary),
    [summary],
  )

  const handlePresentSummary = useCallback(() => {
    trackEvent(
      TRACKING_EVENTS.RUNS.SIMULATIONS_SUMMARY_DISCUSS_WITH_COVI,
      {
        selected_timeframe: summary.selectedTimeframe,
        resolved_timeframe: summary.resolvedTimeframe,
        summary_length: summary.summaryText.length,
        total_runs: summary.currentStats.totalRuns,
        completed_runs: summary.currentStats.completedRuns,
        in_progress_runs: summary.currentStats.inProgressRuns,
        assistant_brand: "sofia",
      },
      { includeIdentity: false },
    )

    openCoviWithSummary({ title: SUMMARY_TITLE, markdown: summaryMarkdown })
  }, [summary, summaryMarkdown, trackEvent])

  // The resolved timeframe is part of the identity: a window shift produces a
  // summary about different runs, which is a summary the user has not been
  // shown yet.
  const pageSummary = useMemo<SofiaPageSummary | null>(
    () =>
      summaryIsLoading ||
      isError ||
      behaviorIsError ||
      summaryMarkdown.trim().length === 0
        ? null
        : {
            title: SUMMARY_TITLE,
            markdown: summaryMarkdown,
            sourceId: `simulations:${summary.resolvedTimeframe}`,
            onPresent: handlePresentSummary,
          },
    [
      behaviorIsError,
      handlePresentSummary,
      isError,
      summary.resolvedTimeframe,
      summaryIsLoading,
      summaryMarkdown,
    ],
  )
  usePublishSofiaPageSummary(pageSummary)
}
